/**
 * Stone Investment Contact Information Section
 *
 * Stone-themed wrapper for the core ContactInformationSection component.
 * Displays contact details with Stone's luxury greyscale aesthetic.
 */

'use client';

import { memo } from 'react';
import {
  ContactInformationSection,
  type ContactInfo,
} from '@/components/contact/ContactInformationSection';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface StoneContactInformationSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
}

export const StoneContactInformationSection = memo(function StoneContactInformationSection({
  messages,
  locale: _locale,
}: StoneContactInformationSectionProps) {
  const stoneMessages = safeGetObject(messages?.stone);
  const infoMessages = safeGetObject(stoneMessages.contactInfo);

  const iconMap: Record<string, React.ReactNode> = {
    email: <Mail size={20} strokeWidth={1.5} className="text-stone-500" />,
    phone: <Phone size={20} strokeWidth={1.5} className="text-stone-500" />,
    address: <MapPin size={20} strokeWidth={1.5} className="text-stone-500" />,
    hours: <Clock size={20} strokeWidth={1.5} className="text-stone-500" />,
  };

  const contactInfo: ContactInfo[] = [
    {
      type: 'email',
      label: safeGetString(infoMessages.emailLabel, 'Email'),
      value: safeGetString(infoMessages.emailValue, 'p.monville@stoneinvestment.fr'),
      icon: iconMap.email,
      href: `mailto:${safeGetString(infoMessages.emailValue, 'p.monville@stoneinvestment.fr')}`,
    },
    {
      type: 'phone',
      label: safeGetString(infoMessages.phoneLabel, 'Phone'),
      value: safeGetString(infoMessages.phoneValue, '+33 6 16 64 16 04'),
      icon: iconMap.phone,
      href: `tel:${safeGetString(infoMessages.phoneValue, '+33 6 16 64 16 04').replace(/\s/g, '')}`,
    },
    {
      type: 'address',
      label: safeGetString(infoMessages.addressLabel, 'Office'),
      value: safeGetString(infoMessages.addressValue, '64, rue Sainte, 13001 Marseille, France'),
      icon: iconMap.address,
    },
    {
      type: 'hours',
      label: safeGetString(infoMessages.hoursLabel, 'Hours'),
      value: safeGetString(infoMessages.hoursValue, 'Mon - Fri, 9:00 - 18:00'),
      icon: iconMap.hours,
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ContactInformationSection
          title={safeGetString(infoMessages.title, 'Contact Information')}
          subtitle={safeGetString(infoMessages.subtitle, undefined)}
          contactInfo={contactInfo}
          className="stone-contact-information"
          renderContactItem={(info) => (
            <div
              key={`${info.type}-${info.label}`}
              className="bg-white border border-stone-200 p-8 transition-colors hover:border-stone-400"
            >
              <div className="flex items-start gap-4">
                {info.icon && (
                  <div className="shrink-0 w-10 h-10 border border-stone-200 flex items-center justify-center">
                    {info.icon}
                  </div>
                )}
                <div>
                  <p
                    className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {info.label}
                  </p>
                  {info.href ? (
                    <a
                      href={info.href}
                      className="text-stone-900 hover:text-stone-600 transition-colors"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p
                      className="text-stone-900"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {info.value}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        />

        <style jsx global>{`
          .stone-contact-information .contact-information-header {
            text-align: center;
            margin-bottom: 3rem;
          }

          .stone-contact-information .contact-information-title {
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 2.5rem;
            font-weight: 400;
            color: rgb(28 25 23);
            margin-bottom: 1rem;
          }

          .stone-contact-information .contact-information-subtitle {
            font-family: 'Montserrat', sans-serif;
            font-size: 1rem;
            color: rgb(87 83 78);
            max-width: 32rem;
            margin: 0 auto;
          }

          .stone-contact-information .contact-information-grid {
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 1.5rem;
          }

          @media (min-width: 768px) {
            .stone-contact-information .contact-information-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (min-width: 1024px) {
            .stone-contact-information .contact-information-grid {
              grid-template-columns: repeat(4, 1fr);
            }
          }
        `}</style>
      </div>
    </section>
  );
});
